/*
 * Eventi del funnel del configuratore 3DLab verso GA4 e Microsoft Clarity.
 *
 * Il modulo non carica nessuna libreria e non contiene ID analytics. Usa
 * l'infrastruttura gia' presente nella pagina (content_for_header):
 *  - Shopify.analytics.publish -> web pixel / custom pixel (GA4);
 *  - gtag o dataLayer (GTM), solo se la pagina li espone;
 *  - clarity() dell'app embed Microsoft Clarity;
 *  - il CustomEvent locale "threedlab:analytics".
 * Non viene mai inviato il payload della configurazione, colori grezzi o dati personali.
 */
(() => {
  'use strict';

  if (window.ThreeDLabConfiguratorTracking) return;

  const SESSION_KEY = '3dlab_configurator_session';
  const DEFAULT_VARIANT_TITLE = 'Default Title';
  const MAX_QUEUED_DELIVERIES = 40;

  const config = window.SHOPIFY_CONFIGURATOR || {};
  const baseProductHandle = String(config.productHandle || '');

  const state = {
    sessionId: '',
    started: false,
    readySignalled: false,
    completed: false,
    mode: 'new',
    stepIndex: 0,
    stepId: '',
    stepName: '',
    totalSteps: 0,
    highestStep: 0,
    closeIntentOpen: false,
    exitHandled: false,
    addedRequestIds: new Set()
  };

  function createId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return `cfg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  function isRepeatNavigation() {
    try {
      const type = performance.getEntriesByType('navigation')[0]?.type;
      return type === 'reload' || type === 'back_forward';
    } catch (_) {
      return false;
    }
  }

  function persistSession() {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        id: state.sessionId,
        handle: baseProductHandle,
        started: state.started,
        completed: state.completed,
        highest: state.highestStep
      }));
    } catch (_) {
      // Senza sessionStorage un refresh viene trattato come nuova sessione.
    }
  }

  // Un refresh o back/forward riprende la stessa sessione e non rilancia configurator_start.
  function restoreSession() {
    if (isRepeatNavigation()) {
      try {
        const stored = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null');
        if (stored && stored.id && stored.handle === baseProductHandle) {
          state.sessionId = String(stored.id);
          state.started = Boolean(stored.started);
          state.completed = Boolean(stored.completed);
          state.highestStep = Number(stored.highest) || 0;
          return;
        }
      } catch (_) {
        // Sessione non leggibile: ne viene creata una nuova.
      }
    }
    state.sessionId = createId();
  }
  restoreSession();

  function getVehicle() {
    const title = String(config.variantTitle || '').trim();
    return title && title !== DEFAULT_VARIANT_TITLE ? title.slice(0, 60) : '';
  }

  function baseParams() {
    return {
      configurator_session_id: state.sessionId,
      base_product_handle: baseProductHandle,
      configurator_mode: state.mode,
      current_step: state.stepIndex || undefined,
      current_step_name: state.stepName,
      configurator_completed: state.completed
    };
  }

  function compact(params) {
    const result = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      result[key] = value;
    });
    return result;
  }

  // Sul configuratore gli script analytics (content_for_header) vengono inseriti dopo il primo
  // render: gli eventi emessi prima restano in coda in memoria e partono all'inserimento.
  const deliveryQueue = [];

  function runTask(task) {
    try {
      task();
    } catch (_) {
      // Il tracking non deve mai rompere il configuratore.
    }
  }

  function deliver(task) {
    if (window.ThreeDLabDeferAnalytics && !window.ThreeDLabAnalyticsReady) {
      if (deliveryQueue.length < MAX_QUEUED_DELIVERIES) deliveryQueue.push(task);
      return;
    }
    runTask(task);
  }

  window.addEventListener('threedlab:analytics-ready', () => {
    deliveryQueue.splice(0, deliveryQueue.length).forEach(runTask);
  }, { once: true });

  function setClarityTag(key, value) {
    const clean = String(value ?? '').trim().slice(0, 100);
    if (!clean) return;
    deliver(() => {
      if (typeof window.clarity === 'function') window.clarity('set', key, clean);
    });
  }

  function send(name, params, beacon) {
    // Web pixel / custom pixel di Shopify: GA4 arriva da qui se esiste un pixel che si
    // iscrive a questi eventi.
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(name, params);
    }

    // GA4/GTM presenti nella pagina (non installati da questo modulo).
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, beacon ? { ...params, transport_type: 'beacon' } : params);
    } else if (window.google_tag_manager && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: name, ...params });
    }

    if (typeof window.clarity === 'function') window.clarity('event', name);

    window.dispatchEvent(new CustomEvent('threedlab:analytics', { detail: { name, params } }));
  }

  function emit(name, extra = {}, { beacon = false } = {}) {
    const params = compact({ ...baseParams(), ...extra });
    deliver(() => send(name, params, beacon));
  }

  function noteHighestStep() {
    if (state.stepIndex > state.highestStep) {
      state.highestStep = state.stepIndex;
      persistSession();
    }
  }

  // Tag di fine percorso: scritti solo quando il valore e' definitivo, cosi' Clarity
  // non accumula valori intermedi per la stessa sessione.
  function setOutcomeTags() {
    setClarityTag('configurator_completed', state.completed ? 'true' : 'false');
    if (state.highestStep) setClarityTag('configurator_highest_step', state.highestStep);
  }

  const tracker = {
    // Da chiamare a ogni render di uno step: emette start al primo, step ai cambi reali.
    viewStep({ index, id, name, total, editing = false }) {
      if (!state.readySignalled) {
        // Segnala che il configuratore e' pronto: da qui possono caricarsi gli script analytics.
        state.readySignalled = true;
        try {
          window.dispatchEvent(new Event('threedlab:configurator-ready'));
        } catch (_) {
          // Event non supportato: scatta il timeout di sicurezza del layout.
        }
      }

      const previousIndex = state.stepIndex;
      const previousId = state.stepId;
      const previousName = state.stepName;

      if (state.stepIndex === index && state.stepId === id) return;

      state.mode = editing ? 'edit' : 'new';
      state.stepIndex = index;
      state.stepId = String(id || '');
      state.stepName = String(name || '');
      state.totalSteps = Number(total) || 0;
      noteHighestStep();

      if (!state.started) {
        state.started = true;
        persistSession();
        emit('configurator_start', {
          total_steps: state.totalSteps,
          configurator_vehicle: getVehicle()
        });
        setClarityTag('configurator_used', 'true');
        setClarityTag('configurator_product', baseProductHandle);
        setClarityTag('configurator_vehicle', getVehicle());
        return;
      }

      if (!previousIndex) return;

      emit('configurator_step', {
        current_step_id: state.stepId,
        previous_step: previousIndex,
        previous_step_id: previousId,
        previous_step_name: previousName,
        highest_step_reached: state.highestStep
      });
    },

    // L'utente ha confermato la configurazione (Aggiungi al carrello / Aggiorna): una volta per sessione.
    complete() {
      if (state.completed) return;
      state.completed = true;
      persistSession();
      emit('configurator_complete', { highest_step_reached: state.highestStep });
      setOutcomeTags();
    },

    // Prodotto custom realmente nel carrello: una volta per requestId.
    addedToCart(requestId) {
      const key = String(requestId || '');
      if (key) {
        if (state.addedRequestIds.has(key)) return;
        state.addedRequestIds.add(key);
      }
      emit('configurator_added_to_cart', { highest_step_reached: state.highestStep }, { beacon: true });
    },

    // Click reale sulla X: intenzione di uscita, non abbandono.
    closeClicked() {
      if (state.closeIntentOpen) return;
      state.closeIntentOpen = true;
      state.exitHandled = false;
      emit('configurator_close_clicked', { highest_step_reached: state.highestStep });
      setClarityTag('configurator_close_step', state.stepName);
    },

    // Popup chiuso senza uscire (Continua, Esc, click sullo sfondo): l'utente prosegue la configurazione.
    exitCancelled() {
      if (!state.closeIntentOpen) return;
      state.closeIntentOpen = false;
      emit('configurator_exit_cancelled', { highest_step_reached: state.highestStep });
    },

    // Uscita volontaria confermata: unico evento di abbandono reale.
    exitConfirmed() {
      if (!state.closeIntentOpen || state.exitHandled) return;
      state.exitHandled = true;
      state.closeIntentOpen = false;
      emit('configurator_exit_confirmed', { highest_step_reached: state.highestStep }, { beacon: true });
      setClarityTag('configurator_exit_step', state.stepName);
      setOutcomeTags();
    }
  };

  window.ThreeDLabConfiguratorTracking = tracker;
})();
